import { useEffect, useState } from "react";
import { useApp } from "./state/AppContext";
import { SessionBrowser } from "./views/SessionBrowser";
import { ReviewWorkspace } from "./views/ReviewWorkspace";
import { LiveMonitor } from "./views/LiveMonitor";
import { ProgressBar, Spinner } from "./components/ui";
import { api } from "./api/client";
import { useAuth } from "./state/AuthContext";
import { AccountPage } from "./views/AccountPage";
import { AdminPage } from "./views/AdminPage";
import { GpuPage } from "./views/GpuPage";

type View = { name: "sessions" } | { name: "review"; sessionId: string } | { name: "live" } | { name: "account" } | { name: "admin" } | { name: "gpu" };

function currentView(): View {
  const hash = location.hash.slice(1);
  if (hash.startsWith("session/")) return { name: "review", sessionId: hash.slice(8) };
  if (hash === "account" || hash === "admin" || hash === "gpu") return {name: hash};
  return hash === "live" ? { name: "live" } : { name: "sessions" };
}

export function App() {
  const { info, loading, error, activeJobs, toasts, dismiss, notify } = useApp();
  const auth = useAuth();
  const [view, setView] = useState<View>(currentView);
  useEffect(() => {
    const change = () => setView(currentView());
    window.addEventListener("hashchange", change);
    return () => window.removeEventListener("hashchange", change);
  }, []);
  const navigate = (next: View) => {
    location.hash = next.name === "review" ? `session/${next.sessionId}` : next.name === "sessions" ? "" : next.name;
    setView(next);
  };

  useEffect(() => {
    if (info?.server_mode !== "local" && view.name === "live") navigate({ name: "sessions" });
  }, [info?.server_mode, view.name]);

  if (loading) {
    return <div className="empty"><Spinner size={22} /></div>;
  }
  if (error || !info) {
    return (
      <div className="empty">
        <h3>The backend is not reachable</h3>
        <p>{error}</p>
        <p className="tiny">
          Start it with <code>./scripts/dev.sh</code>, or run
          <code> uvicorn pads.main:app --port 8787</code> from <code>backend/</code>.
        </p>
        <button onClick={() => location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <div className={`app ${view.name === "review" ? "review-app" : "overview-app"}`}>
      <div className="topbar">
        <span className="brand">
          {info.short_name}<span className="dot">.</span>
        </span>
        <span className="version">v{info.version}</span>
        <div className="nav">
          <button className={["sessions", "review"].includes(view.name) ? "active" : ""} onClick={() => navigate({ name: "sessions" })}>
            Capture library
          </button>
          {info.server_mode === "local" && <button className={view.name === "live" ? "active" : ""} onClick={() => navigate({ name: "live" })}>
            Live capture
          </button>}
          {auth.user?.role === "admin" && <button className={view.name === "admin" ? "active" : ""} onClick={() => navigate({name: "admin"})}>Admin</button>}
          {info.gpu_control && <button className={view.name === "gpu" ? "active" : ""} onClick={() => navigate({name: "gpu"})}>GPU management</button>}
        </div>
        <span className="spacer" />
        {auth.user ? <button className="ghost" onClick={() => navigate({name: "account"})}>{auth.user.name} · Account</button> : <span className="tiny muted">{info.server_mode === "shared" ? "TEAM SERVER" : "LOCAL WORKSPACE"}</span>}
        {activeJobs.length > 0 ? (
          <div className="row" style={{ minWidth: 240 }}>
            <Spinner size={11} />
            <span className="tiny muted truncate" style={{ maxWidth: 140 }}>
              {activeJobs[0].message || activeJobs[0].kind}
              {activeJobs.length > 1 ? ` +${activeJobs.length - 1}` : ""}
            </span>
            <ProgressBar value={activeJobs[0].progress} state={activeJobs[0].state} />
            <button
              className="ghost"
              title="Cancel this job"
              onClick={() => api.cancelJob(activeJobs[0].id).then(() => notify("Job cancelled")).catch(() => undefined)}
            >
              ✕
            </button>
          </div>
        ) : null}
      </div>

      <div className="main">
        {view.name === "sessions" ? (
          <SessionBrowser onOpen={(id) => navigate({ name: "review", sessionId: id })} />
        ) : view.name === "review" ? (
          <ReviewWorkspace key={view.sessionId} sessionId={view.sessionId} onBack={() => navigate({ name: "sessions" })} />
        ) : view.name === "account" ? <AccountPage /> : view.name === "admin" ? <AdminPage /> : view.name === "gpu" ? <GpuPage /> : (
          <LiveMonitor />
        )}
      </div>

      <div className="statusbar">
        <span>{info.server_mode !== "local" ? "Account workspace · uploads from Argus recorder" : `workspace ${info.workspace}`}</span>
        <span className="spacer" />
        <span>{info.capabilities.filter((c) => c.state === "available").length} capabilities available</span>
        <span>{info.capabilities.filter((c) => c.state === "planned").length} planned</span>
      </div>

      <div style={{ position: "fixed", right: 14, bottom: 40, display: "flex", flexDirection: "column", gap: 8, zIndex: 60 }}>
        {toasts.map((t) => (
          <div
            key={t.id}
            onClick={() => dismiss(t.id)}
            style={{
              background: "var(--bg-2)",
              border: `1px solid ${t.kind === "error" ? "var(--bad)" : t.kind === "success" ? "var(--good)" : "var(--line-strong)"}`,
              borderRadius: 5, padding: "8px 12px", maxWidth: 420, cursor: "pointer",
              fontSize: "var(--fs-sm)", boxShadow: "0 8px 24px #0008",
            }}
          >
            {t.message}
          </div>
        ))}
      </div>
    </div>
  );
}
